import React, { useState, useEffect } from 'react';
// Reusable Alert component
const Alert = ({ type, message }: { type: 'error' | 'success', message: string }) => (
  <div className={`w-full flex items-center px-4 py-3 rounded-xl mb-4 font-semibold text-sm shadow-sm border 
    ${type === 'error' ? 'bg-red-50 text-red-800 border-red-200' : 'bg-green-50 text-green-800 border-green-200'}`}
    role="alert">
    {type === 'error' ? (
      <svg className="h-5 w-5 mr-2 text-red-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
    ) : (
      <svg className="h-5 w-5 mr-2 text-green-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
    )}
    <span>{message}</span>
  </div>
);
import { Plus, Search, Dumbbell, Bell, Settings } from 'lucide-react';
import sunshineLogo from './assets/sunshine-logo.jpg';
import StatsCards from './components/StatsCards';
import MembersTable from './components/MembersTable';
import AddMemberModal from './components/AddMemberModal';
import MemberDetailsModal from './components/MemberDetailsModal';
import { db } from './firebase';
import { collection, addDoc, Timestamp, onSnapshot, query, orderBy, DocumentData } from 'firebase/firestore';

export interface Member {
  id: number;
  name: string;
  phone: string;
  email: string;
  gender: string;
  photoUrl: string;
  membershipType: 'Monthly' | 'Quarterly' | 'Yearly';
  plan: string;
  joinDate: string;
  expiryDate: string;
  paymentMethod: 'Cash' | 'Card' | 'UPI';
  paymentStatus: 'Paid' | 'Pending';
  invoiceNumber: string;
  height: string;
  weight: string;
  medical: string;
  fitnessGoal: 'Weight Loss' | 'Muscle Gain' | 'General Fitness';
  emergencyName: string;
  emergencyNumber: string;
  rulesAgreement: boolean;
  termsAgreement: boolean;
}

const GymManagementApp = () => {
  // Get logged-in user from localStorage
  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem('gympro_user') || 'null');
    } catch {
      return null;
    }
  })();

  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem('gympro_user');
    window.location.href = '/login';
  };
  const [members, setMembers] = useState<Member[]>([]);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  // Fetch members from Firestore
  useEffect(() => {
    setFetchLoading(true);
    setFetchError('');
    const q = query(collection(db, 'members'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snapshot) => {
      const data: Member[] = snapshot.docs.map((doc, idx) => ({
        id: doc.id as any, // Firestore doc id as string
        ...doc.data()
      })) as Member[];
      setMembers(data);
      setFetchLoading(false);
    }, (err) => {
      setFetchError('Failed to fetch members: ' + err.message);
      setFetchLoading(false);
    });
    return () => unsub();
  }, []);

  const [showAddMember, setShowAddMember] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');
  const [addSuccess, setAddSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [newMember, setNewMember] = useState<Omit<Member, 'id'>>({
    name: '',
    phone: '',
    email: '',
    gender: '',
    photoUrl: '',
    membershipType: 'Monthly',
    plan: '',
    joinDate: '',
    expiryDate: '',
    paymentMethod: 'Cash',
    paymentStatus: 'Paid',
    invoiceNumber: '',
    height: '',
    weight: '',
    medical: '',
    fitnessGoal: 'General Fitness',
    emergencyName: '',
    emergencyNumber: '',
    rulesAgreement: false,
    termsAgreement: false
  });

  const filteredMembers = members.filter(member =>
    member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.plan?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeMembers = members.length; // All are considered active now
  const totalRevenue = members.length * 1500; // Example: static fee per member
  const newThisMonth = members.filter(m => m.joinDate && new Date(m.joinDate).getMonth() === new Date().getMonth()).length;

  const handleAddMember = async () => {
    if (newMember.name && newMember.email && newMember.phone) {
      setAddLoading(true);
      setAddError('');
      setAddSuccess('');
      try {
        await addDoc(collection(db, 'members'), {
          ...newMember,
          createdAt: Timestamp.now()
        });
        setAddSuccess('Member added successfully!');
        setTimeout(() => {
          setShowAddMember(false);
          setAddSuccess('');
        }, 1200);
        setNewMember({
          name: '',
          phone: '',
          email: '',
          gender: '',
          photoUrl: '',
          membershipType: 'Monthly',
          plan: '',
          joinDate: '',
          expiryDate: '',
          paymentMethod: 'Cash',
          paymentStatus: 'Paid',
          invoiceNumber: '',
          height: '',
          weight: '',
          medical: '',
          fitnessGoal: 'General Fitness',
          emergencyName: '',
          emergencyNumber: '',
          rulesAgreement: false,
          termsAgreement: false
        });
      } catch (err: any) {
        setAddError(err.message || 'Failed to add member.');
      } finally {
        setAddLoading(false);
      }
    }
  };

  const handleNewMemberChange = (field: string, value: string | number | boolean) => {
    setNewMember({ ...newMember, [field]: value });
  };



  // Optionally implement delete from Firestore here
  const deleteMember = (id: number) => {
    // TODO: Implement Firestore delete logic if needed
    alert('Delete functionality not implemented yet.');
  };

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: 'system-ui, -apple-system, \"SF Pro Display\", \"Segoe UI\", Roboto, Ubuntu, sans-serif' }}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div>
                  <img src={sunshineLogo} alt="Sunshine Gym Logo" className="h-12 w-12 object-contain" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
                    Sunshine Gym
                  </h1>
                  <p className="text-gray-500 text-sm font-medium">Fitness Management System</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200">
                <Bell className="h-5 w-5" />
              </button>
              <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200">
                <Settings className="h-5 w-5" />
              </button>
              {/* User info and logout */}
              <div className="flex items-center space-x-2">
                <div className="h-10 w-10 rounded-full bg-gray-900 flex items-center justify-center text-white font-semibold shadow-lg">
                  {user?.email ? user.email.charAt(0).toUpperCase() : 'A'}
                </div>
                <div className="flex flex-col text-right">
                  <span className="text-sm font-bold text-gray-900">{user?.displayName || user?.email || 'Admin'}</span>
                  <button onClick={handleLogout} className="text-xs text-red-600 hover:underline font-semibold mt-1">Logout</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Fetch error/loader */}
        {fetchLoading && (
          <div className="flex justify-center items-center py-8">
            <svg className="animate-spin h-8 w-8 text-gray-900 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
            <span className="font-semibold text-gray-900">Loading members...</span>
          </div>
        )}
        {fetchError && <Alert type="error" message={fetchError} />}
        {/* Stats Cards */}
        <StatsCards
          activeMembers={activeMembers}
          totalMembers={members.length}
          totalRevenue={totalRevenue}
          newThisMonth={newThisMonth}
        />

        {/* Members Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-8 border-b border-gray-100 bg-gray-50">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 tracking-tight mb-2">
                  Members Management
                </h2>
                <p className="text-gray-600 font-medium">Manage your gym community with ease</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
                <div className="relative">
                  <Search className="h-5 w-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search members..."
                    className="pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-all duration-200 w-full min-w-[280px] font-medium shadow-sm"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <button
                  onClick={() => setShowAddMember(true)}
                  className="bg-gray-900 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-200 hover:bg-gray-800 hover:shadow-lg flex items-center gap-2 shadow-sm"
                >
                  <Plus className="h-5 w-5" />
                  Add Member
                </button>
              </div>
            </div>
          </div>
          <MembersTable
            members={filteredMembers}
            onView={(member) => setSelectedMember(member)}
            onDelete={deleteMember}
          />
        </div>
      </div>

      {/* Add Member Modal */}
      {/* Alerts for Add Member */}
      {showAddMember && (
        <div className="fixed top-8 left-0 w-full flex flex-col items-center z-[100] pointer-events-none">
          {addError && <Alert type="error" message={addError} />}
          {addSuccess && <Alert type="success" message={addSuccess} />}
        </div>
      )}
      <AddMemberModal
        show={showAddMember}
        newMember={newMember}
        onChange={handleNewMemberChange}
        onClose={() => setShowAddMember(false)}
        onAdd={handleAddMember}
      />
      {/* Loading indicator overlay */}
      {addLoading && showAddMember && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[200]">
          <div className="bg-white rounded-xl px-8 py-6 shadow-lg flex flex-col items-center">
            <svg className="animate-spin h-8 w-8 text-gray-900 mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
            <span className="font-semibold text-gray-900">Saving member...</span>
          </div>
        </div>
      )}

      {/* Member Details Modal */}
      <MemberDetailsModal
        member={selectedMember}
        onClose={() => setSelectedMember(null)}
      />
    </div>
  );
};

export default GymManagementApp;